<template>
  <app-col-span v-bind="$attrs">
    <label v-if="getLabel" class="d-block mb-1 caption grey--text text--darken-2">{{ getLabel }}</label>

    <div :class="{'rich-text-input--focused': focused, 'rich-text-input--painting': painterActive}"
         class="rich-text-input">
      <!-- Toolbar. mousedown.prevent keeps the caret inside the editable. -->
      <div class="rich-text-input__toolbar">
        <v-btn v-for="cmd in commands" :key="cmd.command"
               :title="cmd.title" class="rich-text-input__btn" icon small
               @mousedown.prevent @click="exec(cmd.command)">
          <v-icon small>{{ cmd.icon }}</v-icon>
        </v-btn>

        <v-divider class="mx-1" vertical/>

        <!-- Font family -->
        <v-menu offset-y>
          <template #activator="{on, attrs}">
            <v-btn class="rich-text-input__btn" icon small v-bind="attrs" v-on="on"
                   :title="$t('attributes.font_family')" @mousedown.prevent>
              <v-icon small>mdi-format-font</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-for="font in fonts" :key="font.value"
                         @mousedown.prevent @click="exec('fontName', font.value)">
              <v-list-item-title :style="{fontFamily: font.value}">{{ font.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Font size -->
        <v-menu offset-y>
          <template #activator="{on, attrs}">
            <v-btn class="rich-text-input__btn" icon small v-bind="attrs" v-on="on"
                   :title="$t('attributes.font_size')" @mousedown.prevent>
              <v-icon small>mdi-format-size</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-for="size in fontSizes" :key="size.value"
                         @mousedown.prevent @click="exec('fontSize', size.value)">
              <v-list-item-title>{{ size.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Colour: presets + full palette -->
        <v-menu offset-y :close-on-content-click="false">
          <template #activator="{on, attrs}">
            <v-btn class="rich-text-input__btn" icon small v-bind="attrs" v-on="on"
                   :title="$t('attributes.color')" @mousedown.prevent>
              <v-icon small>mdi-format-color-text</v-icon>
            </v-btn>
          </template>
          <div class="rich-text-input__colors">
            <div class="rich-text-input__swatches">
              <button v-for="color in colors" :key="color" :style="{background: color}"
                      class="rich-text-input__swatch" type="button"
                      @mousedown.prevent @click="exec('foreColor', color)"/>
            </div>
            <label class="rich-text-input__picker" @mousedown.prevent="rememberSelection">
              <input :value="customColor" type="color" @input="pickColor($event.target.value)">
              <span>{{ $t('pick_color') }}</span>
            </label>
          </div>
        </v-menu>

        <v-divider class="mx-1" vertical/>

        <!-- Link -->
        <v-menu v-model="linkMenu" :close-on-content-click="false" offset-y>
          <template #activator="{on, attrs}">
            <v-btn class="rich-text-input__btn" icon small v-bind="attrs" v-on="on"
                   :title="$t('insert_link')" @mousedown.prevent="rememberSelection">
              <v-icon small>mdi-link-variant</v-icon>
            </v-btn>
          </template>
          <div class="rich-text-input__popup">
            <v-text-field v-model="linkUrl" :label="$t('link_url')"
                          autofocus dense hide-details outlined
                          placeholder="https://example.com"
                          @keyup.enter="applyLink"/>
            <div class="mt-2 d-flex" style="gap:6px">
              <v-btn color="primary" small @mousedown.prevent @click="applyLink">{{ $t('done') }}</v-btn>
              <v-btn small text @mousedown.prevent @click="linkMenu = false">{{ $t('cancel') }}</v-btn>
            </div>
          </div>
        </v-menu>

        <v-btn :title="$t('remove_link')" class="rich-text-input__btn" icon small
               @mousedown.prevent @click="exec('unlink')">
          <v-icon small>mdi-link-variant-off</v-icon>
        </v-btn>

        <!-- Insert image: downscaled and embedded as a data-uri. -->
        <v-btn :title="$t('insert_image')" class="rich-text-input__btn" icon small
               @mousedown.prevent="rememberSelection" @click="pickImage">
          <v-icon small>mdi-image-outline</v-icon>
        </v-btn>
        <input ref="imageInput" accept="image/png,image/jpeg,image/gif,image/webp"
               class="d-none" type="file" @change="onImagePicked">

        <!-- Tables -->
        <v-menu v-model="tableMenu" :close-on-content-click="false" offset-y>
          <template #activator="{on, attrs}">
            <v-btn class="rich-text-input__btn" icon small v-bind="attrs" v-on="on"
                   :title="$t('insert_table')" @mousedown.prevent="rememberSelection">
              <v-icon small>mdi-table</v-icon>
            </v-btn>
          </template>
          <div class="rich-text-input__popup">
            <div class="d-flex" style="gap:8px">
              <v-text-field v-model.number="tableRows" :label="$t('table_rows')"
                            dense hide-details min="1" outlined type="number"/>
              <v-text-field v-model.number="tableCols" :label="$t('table_cols')"
                            dense hide-details min="1" outlined type="number"/>
            </div>
            <div class="mt-2 d-flex flex-wrap" style="gap:6px">
              <v-btn color="primary" small @mousedown.prevent @click="insertTable">
                {{ $t('insert_table') }}
              </v-btn>
              <v-btn small text @mousedown.prevent @click="tableMenu = false">{{ $t('cancel') }}</v-btn>
            </div>
            <v-divider class="my-2"/>
            <div class="d-flex flex-wrap" style="gap:6px">
              <v-btn outlined small @mousedown.prevent @click="addRow">{{ $t('table_add_row') }}</v-btn>
              <v-btn outlined small @mousedown.prevent @click="addColumn">{{ $t('table_add_col') }}</v-btn>
              <v-btn color="error" outlined small @mousedown.prevent @click="deleteRow">
                {{ $t('table_del_row') }}
              </v-btn>
              <v-btn color="error" outlined small @mousedown.prevent @click="deleteColumn">
                {{ $t('table_del_col') }}
              </v-btn>
            </div>

            <!-- Width of the table the caret is in. -->
            <v-divider class="my-2"/>
            <div class="caption grey--text mb-1">{{ $t('table_width') }}</div>
            <div class="d-flex flex-wrap" style="gap:6px">
              <v-btn outlined small @mousedown.prevent @click="setTableWidth('full')">
                {{ $t('table_width_full') }}
              </v-btn>
              <v-btn outlined small @mousedown.prevent @click="setTableWidth('center')">
                {{ $t('table_width_center') }}
              </v-btn>
              <v-btn outlined small @mousedown.prevent @click="setTableWidth('auto')">
                {{ $t('table_width_auto') }}
              </v-btn>
            </div>

            <!-- Cell background. execCommand only paints selected TEXT, so the
                 colour is written onto the cells themselves. -->
            <v-divider class="my-2"/>
            <div class="caption grey--text mb-1">{{ $t('table_fill') }}</div>
            <v-btn-toggle v-model="fillScope" class="mb-2" dense mandatory>
              <v-btn small value="cell" @mousedown.prevent>{{ $t('table_scope_cell') }}</v-btn>
              <v-btn small value="row" @mousedown.prevent>{{ $t('table_scope_row') }}</v-btn>
              <v-btn small value="col" @mousedown.prevent>{{ $t('table_scope_col') }}</v-btn>
              <v-btn small value="table" @mousedown.prevent>{{ $t('table_scope_table') }}</v-btn>
            </v-btn-toggle>
            <div class="rich-text-input__swatches">
              <button v-for="color in fillColors" :key="'fill-' + color" :style="{background: color}"
                      class="rich-text-input__swatch" type="button"
                      @mousedown.prevent @click="applyFill(color)"/>
            </div>
            <div class="d-flex align-center flex-wrap mt-1" style="gap:6px">
              <label class="rich-text-input__picker" @mousedown.prevent>
                <input :value="customFill" type="color" @input="applyFill($event.target.value)">
                <span>{{ $t('pick_color') }}</span>
              </label>
              <v-btn outlined small @mousedown.prevent @click="applyFill(null)">
                {{ $t('table_fill_none') }}
              </v-btn>
            </div>
          </div>
        </v-menu>

        <!-- Format painter (shared across every editor on the page) -->
        <v-btn :class="{'primary white--text': painterActive}"
               :title="$t('format_painter')"
               class="rich-text-input__btn" icon small
               @mousedown.prevent @click="togglePainter">
          <v-icon small>mdi-format-paint</v-icon>
        </v-btn>

        <v-spacer/>
        <slot name="toolbar-append"/>
      </div>

      <div ref="editable"
           :style="{minHeight: minHeight}"
           class="rich-text-input__body"
           contenteditable="true"
           @blur="onBlur"
           @focus="onFocus"
           @input="onTyping"
           @keydown="onKeydown"
           @keyup="rememberSelection"
           @mouseup="onMouseUp"/>
    </div>
  </app-col-span>
</template>

<script>
import Vue from 'vue';
import {normalizeChips} from '@helpers/fieldChips';
import UndoHistoryMixin from '@mixins/UndoHistoryMixin';

/**
 * Format-painter clipboard shared by every editor instance, so a format can be
 * copied in one field and painted in another.
 */
const painter = Vue.observable({armed: false, format: null});

/**
 * Minimal rich-text field built on contenteditable + document.execCommand,
 * so the letter templates can be formatted without pulling in an editor
 * package. Emits HTML through v-model.
 */
export default {
  name: 'RichTextInput',
  mixins: [UndoHistoryMixin],
  props: {
    value: {default: () => ''},
    name: {},
    label: {},
    minHeight: {default: () => '220px'}
  },
  data() {
    return {
      focused: false,
      savedRange: null,
      linkMenu: false,
      linkUrl: '',
      tableMenu: false,
      tableRows: 2,
      tableCols: 2,
      customColor: '#000000',
      customFill: '#d9d9d9',
      // Which part of the table the next fill colour paints.
      fillScope: 'cell',
      commands: [
        {command: 'bold', icon: 'mdi-format-bold', title: 'Bold'},
        {command: 'italic', icon: 'mdi-format-italic', title: 'Italic'},
        {command: 'underline', icon: 'mdi-format-underline', title: 'Underline'},
        {command: 'justifyRight', icon: 'mdi-format-align-right', title: 'Right'},
        {command: 'justifyCenter', icon: 'mdi-format-align-center', title: 'Center'},
        {command: 'justifyLeft', icon: 'mdi-format-align-left', title: 'Left'},
        {command: 'justifyFull', icon: 'mdi-format-align-justify', title: 'Justify'},
        {command: 'insertUnorderedList', icon: 'mdi-format-list-bulleted', title: 'Bullets'},
        {command: 'insertOrderedList', icon: 'mdi-format-list-numbered', title: 'Numbers'},
        {command: 'removeFormat', icon: 'mdi-format-clear', title: 'Clear'}
      ],
      fonts: [
        {value: "'Kufi','Sans'", text: 'افتراضي'},
        {value: "'Kufi'", text: 'Kufi'},
        {value: "'Sans'", text: 'Sans'},
        {value: 'Arial', text: 'Arial'},
        {value: 'Tahoma', text: 'Tahoma'},
        {value: "'Times New Roman'", text: 'Times New Roman'},
        {value: "'Courier New'", text: 'Courier New'}
      ],
      fontSizes: [
        {value: '2', text: 'صغير'},
        {value: '3', text: 'عادي'},
        {value: '5', text: 'كبير'},
        {value: '6', text: 'كبير جداً'}
      ],
      colors: ['#000000', '#c62828', '#1565c0', '#2e7d32', '#ef6c00', '#6a1b9a', '#616161'],
      // Backgrounds are behind text, so these are the pale end of the palette
      // (#d9d9d9 is the shade the letter's own label column already uses).
      fillColors: ['#d9d9d9', '#f2f2f2', '#fde0e0', '#e3f2fd', '#e8f5e9', '#fff8e1', '#ede7f6']
    };
  },
  computed: {
    getLabel() {
      const k = this.label !== undefined ? this.label : this.name;
      return k ? this.parseAttribute(k) : '';
    },
    painterActive() {
      return painter.armed;
    }
  },
  watch: {
    value(v) {
      // Only write back when the change came from outside, so typing is not disturbed.
      if (this.$refs.editable && v !== this.$refs.editable.innerHTML) {
        this.$refs.editable.innerHTML = v || '';
        // A whole new document - the draft loading, or the settings being
        // reset. Undoing back past it would put someone else's text on screen.
        this.historyReset();
      }
    }
  },
  mounted() {
    if (this.$refs.editable) this.$refs.editable.innerHTML = this.value || '';
    this.historyReset();
  },
  methods: {
    /** Push what is in the editable out through v-model. */
    sync() {
      this.rememberSelection();
      // A field chip pasted from elsewhere in the text comes back through the
      // browser's sanitiser, which can strip the parts that make it behave as
      // one - the remove button among them. Put those back before the value
      // leaves the editor.
      normalizeChips(this.$refs.editable);
      this.$emit('input', this.$refs.editable.innerHTML);
    },
    /** The user typed: one undo step per burst, not one per keystroke. */
    onTyping() {
      this.sync();
      this.historySchedule();
    },
    /**
     * The editor changed the content itself - a chip, a table column, a fill.
     * That is one step, and it is finished the moment it is made.
     */
    onInput() {
      this.sync();
      this.historyRecord();
    },
    onFocus() {
      this.focused = true;
      // Let the parent know which editor the caret is in (token insertion).
      this.$emit('focus');
    },
    onBlur() {
      this.focused = false;
      this.rememberSelection();
      // Close off whatever was being typed, so the next change is a step of
      // its own rather than joining onto it.
      this.historyFlush();
      this.$emit('blur');
    },
    onMouseUp() {
      this.rememberSelection();
      this.historyFlush();
      // Painter is armed: paint whatever the user just selected, in any editor.
      if (painter.armed && painter.format) {
        this.applyCopiedFormat();
      }
    },
    /**
     * Undo and redo are handled here rather than by the browser.
     *
     * Half of what this editor does - inserting a chip, adding a column,
     * painting a cell - is written straight into the DOM, where the browser's
     * own history never sees it. Left to it, Ctrl+Z would skip the chip that
     * was just inserted and undo the sentence before it instead.
     */
    onKeydown(e) {
      const action = this.historyHotkey(e);
      if (!action) return;
      e.preventDefault();
      action === 'redo' ? this.historyRedo() : this.historyUndo();
    },
    rememberSelection() {
      const sel = window.getSelection();
      if (sel && sel.rangeCount && this.$refs.editable.contains(sel.anchorNode)) {
        this.savedRange = sel.getRangeAt(0).cloneRange();
      }
    },
    restoreSelection() {
      this.$refs.editable.focus();
      if (!this.savedRange) return;
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(this.savedRange);
    },
    exec(command, argument = null) {
      this.restoreSelection();
      // Where things stood before this button was pressed, so it can be undone
      // on its own rather than together with whatever was typed before it.
      this.historyRecord();
      // Emit inline CSS rather than legacy <font> tags.
      document.execCommand('styleWithCSS', false, true);
      document.execCommand(command, false, argument);
      this.onInput();
    },
    pickColor(color) {
      this.customColor = color;
      this.exec('foreColor', color);
    },

    /* ---------------------------------------------------------- painter -- */

    /** Arm the painter with the format at the caret, or disarm it. */
    togglePainter() {
      if (painter.armed) {
        painter.armed = false;
        return;
      }
      this.restoreSelection();
      const format = this.captureFormat();
      if (!format) return;
      painter.format = format;
      painter.armed = true;
    },
    /** Read the effective character formatting at the caret/selection. */
    captureFormat() {
      const sel = window.getSelection();
      let node = sel && sel.anchorNode;
      if (node && node.nodeType === 3) node = node.parentElement;
      if (!node || !this.$refs.editable.contains(node)) return null;
      const cs = window.getComputedStyle(node);
      return {
        'font-weight': cs.fontWeight,
        'font-style': cs.fontStyle,
        'text-decoration': cs.textDecorationLine || 'none',
        'color': cs.color,
        'font-size': cs.fontSize,
        'font-family': cs.fontFamily
      };
    },
    /** Wrap the current selection in a span carrying the captured format. */
    applyCopiedFormat() {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
      const range = sel.getRangeAt(0);
      if (!this.$refs.editable.contains(range.commonAncestorContainer)) return;

      this.historyRecord();
      const holder = document.createElement('div');
      holder.appendChild(range.cloneContents());
      const style = Object.keys(painter.format)
          .map(key => `${key}:${painter.format[key]}`)
          .join(';');
      document.execCommand('styleWithCSS', false, true);
      document.execCommand('insertHTML', false, `<span style="${style}">${holder.innerHTML}</span>`);
      painter.armed = false;
      this.onInput();
    },

    /* ----------------------------------------------------------- tables -- */

    insertTable() {
      const rows = Math.max(1, parseInt(this.tableRows, 10) || 1);
      const cols = Math.max(1, parseInt(this.tableCols, 10) || 1);
      const cell = '<td style="border:1px solid #444;padding:4px">&nbsp;</td>';
      const row = `<tr>${cell.repeat(cols)}</tr>`;
      // Tag the new table so the caret can be dropped into its first cell:
      // insertHTML leaves the caret after the table, which would make the
      // row/column buttons no-ops until the user clicked inside.
      const marker = 'new-' + Date.now();
      const html = `<table data-new="${marker}" style="width:100%;border-collapse:collapse">`
          + row.repeat(rows) + '</table><p>&nbsp;</p>';
      this.restoreSelection();
      this.historyRecord();
      document.execCommand('insertHTML', false, html);
      const table = this.$refs.editable.querySelector(`table[data-new="${marker}"]`);
      if (table) {
        table.removeAttribute('data-new');
        this.focusCell(table.rows[0] && table.rows[0].children[0]);
      }
      this.tableMenu = false;
      this.onInput();
    },
    /** The table cell holding the caret, or null when outside a table. */
    currentCell() {
      const range = this.savedRange;
      let node = range ? range.startContainer : null;
      if (node && node.nodeType === 3) node = node.parentElement;
      while (node && node !== this.$refs.editable && node.tagName !== 'TD' && node.tagName !== 'TH') {
        node = node.parentElement;
      }
      return node && (node.tagName === 'TD' || node.tagName === 'TH') ? node : null;
    },
    /**
     * Move the caret into a cell and re-save the range. Structural edits detach
     * the previously saved range, which would make every later table operation
     * a silent no-op.
     */
    focusCell(cell) {
      if (!cell) return;
      const range = document.createRange();
      range.selectNodeContents(cell);
      range.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      this.savedRange = range.cloneRange();
    },
    addRow() {
      const cell = this.currentCell();
      if (!cell) return;
      this.historyRecord();
      const row = cell.parentElement;
      const clone = row.cloneNode(true);
      [...clone.children].forEach(td => (td.innerHTML = '&nbsp;'));
      row.parentNode.insertBefore(clone, row.nextSibling);
      this.focusCell(clone.children[[...row.children].indexOf(cell)] || clone.children[0]);
      this.onInput();
    },
    deleteRow() {
      const cell = this.currentCell();
      if (!cell) return;
      this.historyRecord();
      const row = cell.parentElement;
      const table = row.closest('table');
      const columnIndex = [...row.children].indexOf(cell);
      const rowIndex = row.rowIndex;
      if (table && table.rows.length <= 1) {
        table.remove();
        this.savedRange = null;
      } else {
        row.remove();
        const next = table.rows[rowIndex] || table.rows[table.rows.length - 1];
        this.focusCell(next && (next.children[columnIndex] || next.children[0]));
      }
      this.onInput();
    },
    addColumn() {
      const cell = this.currentCell();
      if (!cell) return;
      this.historyRecord();
      const row = cell.parentElement;
      const index = [...row.children].indexOf(cell);
      const table = cell.closest('table');
      [...table.rows].forEach(r => {
        // Copy each row's own cell in that column rather than building a bare
        // <td>: that keeps <th> a <th>, and carries the borders, padding,
        // alignment and fill the rest of the column already has. addRow has
        // always cloned; this did not, so a new column came out unstyled.
        const reference = r.children[index] || r.children[r.children.length - 1];
        let td;
        if (reference) {
          td = reference.cloneNode(false);   // tag and attributes, no content
          td.removeAttribute('colspan');
          td.removeAttribute('rowspan');
        } else {
          td = document.createElement('td');
          td.setAttribute('style', 'border:1px solid #444;padding:4px');
        }
        td.innerHTML = '&nbsp;';
        r.insertBefore(td, r.children[index + 1] || null);
      });
      this.focusCell(row.children[index + 1]);
      this.onInput();
    },
    deleteColumn() {
      const cell = this.currentCell();
      if (!cell) return;
      this.historyRecord();
      const row = cell.parentElement;
      const index = [...row.children].indexOf(cell);
      const table = cell.closest('table');
      if (table.rows[0] && table.rows[0].children.length <= 1) {
        table.remove();
        this.savedRange = null;
      } else {
        [...table.rows].forEach(r => r.children[index] && r.children[index].remove());
        this.focusCell(row.children[index] || row.children[row.children.length - 1]);
      }
      this.onInput();
    },
    /** The table holding the caret, or null when the caret is outside one. */
    currentTable() {
      const cell = this.currentCell();
      return cell ? cell.closest('table') : null;
    },
    /**
     * Page-wide, centred, or only as wide as its content.
     * Centring needs `width:auto` — a 100%-wide table has nothing to centre in.
     */
    setTableWidth(mode) {
      const table = this.currentTable();
      if (!table) return;
      this.historyRecord();
      table.style.width = mode === 'full' ? '100%' : 'auto';
      table.style.marginLeft = mode === 'center' ? 'auto' : '0';
      table.style.marginRight = mode === 'center' ? 'auto' : '0';
      this.focusCell(this.currentCell());
      this.onInput();
    },
    /**
     * Paint the cells covered by the current scope. `null` clears the colour.
     *
     * The colour goes on each td/th rather than on the <table>: the PDF engine
     * does not reliably paint a table-level background behind its own cells.
     */
    applyFill(color) {
      const cell = this.currentCell();
      if (!cell) return;
      this.historyRecord();
      const table = cell.closest('table');
      const row = cell.parentElement;
      const index = [...row.children].indexOf(cell);
      let cells = [cell];

      if (this.fillScope === 'row') {
        cells = [...row.children];
      } else if (this.fillScope === 'col') {
        cells = [...table.rows].map(r => r.children[index]).filter(Boolean);
      } else if (this.fillScope === 'table') {
        cells = [...table.querySelectorAll('td,th')];
      }

      cells.forEach(td => (td.style.backgroundColor = color || ''));
      if (color) this.customFill = color;
      this.focusCell(cell);
      this.onInput();
    },

    /* ------------------------------------------------------------ links -- */

    /**
     * Turn the saved selection into a link. With no selection the URL itself
     * is inserted as the link text, so the button is never a no-op.
     */
    applyLink() {
      const url = this.normalizeUrl(this.linkUrl);
      if (!url) {
        this.linkMenu = false;
        return;
      }
      this.restoreSelection();
      this.historyRecord();
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        document.execCommand('insertHTML', false, `<a href="${url}">${this.linkUrl.trim()}</a>`);
      } else {
        document.execCommand('createLink', false, url);
      }
      this.onInput();
      this.linkUrl = '';
      this.linkMenu = false;
    },
    /** Default to https:// and reject script-bearing URLs. */
    normalizeUrl(value) {
      const url = (value || '').trim();
      if (!url) return '';
      if (/^\s*javascript:/i.test(url)) return '';
      return /^([a-z][a-z0-9+.-]*:|#|\/|mailto:)/i.test(url) ? url : `https://${url}`;
    },
    /* ----------------------------------------------------------- images -- */

    pickImage() {
      this.rememberSelection();
      this.$refs.imageInput.value = '';
      this.$refs.imageInput.click();
    },
    onImagePicked(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      // SVG can carry script, and the server strips it anyway.
      if (!/^image\/(png|jpeg|gif|webp)$/i.test(file.type)) return;
      const reader = new FileReader();
      reader.onload = () => this.downscale(reader.result, dataUri => this.insertImage(dataUri));
      reader.readAsDataURL(file);
    },
    /**
     * Shrink oversized images before embedding: a full-resolution photo as a
     * data-uri would bloat the template row and the generated PDF.
     */
    downscale(dataUri, done) {
      const MAX = 1000;
      const img = new Image();
      img.onload = () => {
        if (img.width <= MAX && img.height <= MAX) return done(dataUri);
        const scale = MAX / Math.max(img.width, img.height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        // PNG keeps transparency (logos/stamps); JPEG would turn it black.
        done(canvas.toDataURL('image/png'));
      };
      img.onerror = () => done(dataUri);
      img.src = dataUri;
    },
    insertImage(dataUri) {
      this.restoreSelection();
      this.historyRecord();
      document.execCommand('insertHTML', false,
          `<img src="${dataUri}" style="max-width:100%">`);
      this.onInput();
    },
    /** Insert plain text (a {token}) at the caret. Used by the field picker. */
    insertText(text) {
      this.restoreSelection();
      this.historyRecord();
      document.execCommand('insertText', false, text);
      this.onInput();
    },
    /**
     * Insert markup at the caret - a field chip, for instance - and leave the
     * caret after it.
     *
     * Not execCommand('insertHTML'): after that, the caret lands wherever the
     * browser feels like, and a chip is contenteditable="false", so there is
     * nothing editable behind it for the caret to sit in - the next thing typed
     * goes in front of the chip instead of after it. A zero-width space gives
     * it somewhere to land; chipsToTokens strips those on the way out.
     */
    insertHtml(html) {
      this.restoreSelection();
      // The text as it was, so Ctrl+Z takes the chip back out again.
      this.historyRecord();

      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) {
        document.execCommand('insertHTML', false, html);
        this.onInput();
        return;
      }

      // Not execCommand('insertHTML'): with the caret at the end of a
      // paragraph it lifts the inserted node out of the block entirely, and a
      // chip then sits on a line of its own under the text.
      //
      // Deleting first, then normalising: a selection has to be replaced, and
      // normalising a non-collapsed range would collapse it and leave the
      // selected text sitting there.
      let range = sel.getRangeAt(0).cloneRange();
      range.deleteContents();
      range = this.textPositionedRange(range);

      const fragment = range.createContextualFragment(html);
      const inserted = fragment.lastChild;
      range.insertNode(fragment);

      // A chip is contenteditable="false", so there is nothing behind it for
      // the caret to sit in and the next thing typed would land in front of
      // it. chipsToTokens strips these on the way out.
      const caretHome = document.createTextNode('​');
      if (inserted && inserted.parentNode) {
        inserted.parentNode.insertBefore(caretHome, inserted.nextSibling);
      } else {
        range.insertNode(caretHome);
      }

      const after = document.createRange();
      after.setStart(caretHome, caretHome.length);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);

      this.$refs.editable.focus();
      this.rememberSelection();
      this.onInput();
    },
    /**
     * The same position, expressed against a text node rather than between an
     * element's children.
     *
     * insertNode at an element boundary puts the node beside the block instead
     * of inside it - which is exactly what happens with the caret at the end of
     * a paragraph, where the range reads as (P, childCount).
     *
     * @param {Range} range
     * @returns {Range}
     */
    textPositionedRange(range) {
      const positioned = range.cloneRange();
      const node = positioned.startContainer;
      if (node.nodeType === 3) return positioned;

      const before = node.childNodes[positioned.startOffset - 1];
      if (before && before.nodeType === 3) {
        positioned.setStart(before, before.textContent.length);
        positioned.collapse(true);
        return positioned;
      }
      const at = node.childNodes[positioned.startOffset];
      if (at && at.nodeType === 3) {
        positioned.setStart(at, 0);
        positioned.collapse(true);
        return positioned;
      }
      return positioned;
    },
    /**
     * Push the editable's current content back through v-model.
     *
     * Needed after changing the DOM directly (replacing a chip, say): the
     * browser fires no `input` event for that, so v-model would keep the
     * content from before the change.
     */
    syncFromDom() {
      this.onInput();
    },
    /**
     * Run a change the parent makes to the editable's DOM as one undo step:
     * what was there is written down first, and what results after.
     *
     * @param {Function} change
     */
    mutateDom(change) {
      this.historyRecord();
      change();
      this.onInput();
    },

    /* ------------------------------------------------------ undo history -- */

    historySnapshot() {
      const el = this.$refs.editable;
      if (!el) return null;
      const html = el.innerHTML;
      // With the caret outside the editable - a toolbar button was pressed -
      // there is nothing to read, so the position already recorded for this
      // same content stands.
      const current = this.historyStack[this.historyAt];
      const caret = this.caretSpec()
          || (current && current.key === html ? current.caret : null);
      return {key: html, html, caret};
    },
    historyApply(state) {
      const el = this.$refs.editable;
      if (!el) return;
      // Where the user is now, for a state that never recorded a position of
      // its own - the document as it was loaded, say. Better than dropping
      // them at the very start of the text.
      const fallback = this.caretSpec();
      el.innerHTML = state.html;
      this.applyCaret(state.caret || fallback);
      this.$emit('input', el.innerHTML);
    },
    /**
     * Where the selection is, in a form that survives the content being
     * replaced: offsets alone would not, since restoring builds every node
     * afresh.
     *
     * @returns {?{start: object, end: object}}
     */
    caretSpec() {
      const el = this.$refs.editable;
      const sel = window.getSelection();
      if (!el || !sel || !sel.rangeCount) return null;
      const range = sel.getRangeAt(0);
      if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return null;

      const start = this.pathOf(range.startContainer, range.startOffset);
      const end = range.collapsed ? start : this.pathOf(range.endContainer, range.endOffset);
      return start && end ? {start, end} : null;
    },
    /** A node as the child indexes leading to it from the editable. */
    pathOf(node, offset) {
      const el = this.$refs.editable;
      const path = [];
      let current = node;
      while (current && current !== el) {
        const parent = current.parentNode;
        if (!parent) return null;
        path.unshift(Array.prototype.indexOf.call(parent.childNodes, current));
        current = parent;
      }
      return current === el ? {path, offset} : null;
    },
    nodeAt(spec) {
      let node = this.$refs.editable;
      for (let i = 0; i < spec.path.length; i++) {
        node = node.childNodes[spec.path[i]];
        if (!node) return null;
      }
      return node;
    },
    applyCaret(caret) {
      const el = this.$refs.editable;
      el.focus();
      if (!caret) return;
      const startNode = this.nodeAt(caret.start);
      const endNode = this.nodeAt(caret.end);
      if (!startNode || !endNode) return;

      const limit = node => (node.nodeType === 3 ? node.textContent.length : node.childNodes.length);
      try {
        const range = document.createRange();
        range.setStart(startNode, Math.min(caret.start.offset, limit(startNode)));
        range.setEnd(endNode, Math.min(caret.end.offset, limit(endNode)));
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        this.savedRange = range.cloneRange();
      } catch (e) {
        // The shape shifted under it; having the focus back is enough.
      }
    }
  }
};
</script>

<!-- Unscoped on purpose: chips are written into the editable with innerHTML, so
     they never get the scoping attribute and scoped rules would not reach them. -->
<style>
.app-field-chip__x {
  display: none;
  margin-inline-start: 5px;
  font-weight: bold;
  cursor: pointer;
  opacity: .6;
}

.app-field-chip:hover .app-field-chip__x {
  display: inline;
}

.app-field-chip__x:hover {
  opacity: 1;
}
</style>

<style scoped>
.rich-text-input {
  border: 1px solid rgba(0, 0, 0, .38);
  border-radius: 4px;
}

.rich-text-input--focused {
  border-color: var(--v-primary-base, #1976d2);
}

.rich-text-input--painting .rich-text-input__body {
  cursor: cell;
}

.rich-text-input__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  border-bottom: 1px solid rgba(0, 0, 0, .12);
  background: rgba(0, 0, 0, .03);
}

.rich-text-input__body {
  padding: 10px 12px;
  outline: none;
  overflow-y: auto;
  line-height: 1.8;
}

/* Lists need explicit markers/indent: Vuetify's reset strips them. */
.rich-text-input__body >>> ul,
.rich-text-input__body >>> ol {
  padding-inline-start: 26px;
  margin: 6px 0;
}

.rich-text-input__body >>> ul {
  list-style: disc outside;
}

.rich-text-input__body >>> ol {
  list-style: decimal outside;
}

.rich-text-input__body >>> li {
  display: list-item;
}

.rich-text-input__body >>> table {
  width: 100%;
  border-collapse: collapse;
}

.rich-text-input__colors {
  padding: 8px;
  background: #fff;
}

.rich-text-input__popup {
  padding: 10px;
  background: #fff;
  min-width: 300px;
}

.rich-text-input__swatches {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}

.rich-text-input__picker {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
}

.rich-text-input__picker input[type="color"] {
  width: 34px;
  height: 24px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, .2);
  border-radius: 3px;
  cursor: pointer;
  background: none;
}

.rich-text-input__swatch {
  width: 22px;
  height: 22px;
  border: 1px solid rgba(0, 0, 0, .2);
  border-radius: 3px;
  cursor: pointer;
}
</style>
